import type Token from 'markdown-it/lib/token';
import type {Run} from '~/commands/build';
import type {CrawlerConfig, CrawlerNotifications} from './types';

import {dirname, join} from 'node:path';
import {load as yamlLoad} from 'js-yaml';
import MarkdownIt from 'markdown-it';

import {INCLUDE_REGEX, findLink} from '~/core/markdown';
import {isExternalHref, isFenceClose, matchFenceOpen, normalizePath, walkLinks} from '~/core/utils';

import {FILE_BLOCK_REGEX, IPV4_HOST_RE, REGEXP_LITERAL} from './constants';

const md = new MarkdownIt({html: true, linkify: true});
const mdNoLinkify = new MarkdownIt({html: true, linkify: false});

function walkTokens(tokens: Token[], links: Set<string>): void {
    for (const token of tokens) {
        if (token.type === 'link_open') {
            const href = token.attrGet('href');

            if (href && isExternalHref(href)) {
                links.add(href);
            }
        } else if (token.type === 'image') {
            const src = token.attrGet('src');

            if (src && isExternalHref(src)) {
                links.add(src);
            }
        }

        if (token.children) {
            walkTokens(token.children, links);
        }
    }
}

export function extractExternalLinks(content: string, {linkify = true} = {}): string[] {
    const links = new Set<string>();
    const env: {references?: Record<string, {href: string}>} = {};

    walkTokens((linkify ? md : mdNoLinkify).parse(content, env), links);

    for (const ref of Object.values(env.references ?? {})) {
        if (ref.href && isExternalHref(ref.href)) {
            links.add(ref.href);
        }
    }

    for (const match of content.matchAll(FILE_BLOCK_REGEX)) {
        if (isExternalHref(match[1])) {
            links.add(match[1]);
        }
    }

    return [...links];
}

function stripFencedBlocks(content: string): string {
    const lines = content.split('\n');
    const out: string[] = [];
    let markup = '';

    for (const line of lines) {
        // Indent is not limited: fences inside lists, tabs and cuts are
        // indented deeper than the three spaces CommonMark allows, and a
        // link shown inside such a block is still an example, not a link
        // worth crawling.
        const normalized = line.replace(/\r$/, '').trimStart();

        if (markup) {
            if (isFenceClose(normalized, markup)) {
                markup = '';
            }

            continue;
        }

        const fence = matchFenceOpen(normalized);

        if (fence) {
            markup = fence.markup;
        } else {
            out.push(line);
        }
    }

    return out.join('\n');
}

function stripNonContent(content: string): string {
    return stripFencedBlocks(content.replace(/<!--[\s\S]*?-->/g, '')).replace(/`[^`\n]+`/g, '');
}

export function collectExternalLinksFromYaml(content: string): string[] {
    const links: string[] = [];

    try {
        const data = yamlLoad(content);

        if (data && typeof data === 'object') {
            walkLinks(data, (value) => {
                if (isExternalHref(value)) {
                    links.push(value);
                }
            });
        }
    } catch {}

    return links;
}

export function extractIncludePaths(content: string): string[] {
    const stripped = stripNonContent(content);
    const paths: string[] = [];

    for (const match of stripped.matchAll(INCLUDE_REGEX)) {
        const link = findLink(match[0]);

        if (link && !isExternalHref(link)) {
            paths.push(link);
        }
    }

    return paths;
}

export async function collectLinks(
    run: Run,
    filePath: string,
    visited = new Set<string>(),
): Promise<string[]> {
    const normalized = normalizePath(filePath);

    if (visited.has(normalized)) {
        return [];
    }

    visited.add(normalized);

    let content: string;

    try {
        content = await run.read(join(run.input, normalized) as AbsolutePath);
    } catch {
        return [];
    }

    const isYaml = normalized.endsWith('.yaml') || normalized.endsWith('.yml');
    const links = isYaml
        ? [
              ...extractExternalLinks(content, {linkify: false}),
              ...collectExternalLinksFromYaml(content),
          ]
        : extractExternalLinks(content);

    if (normalized.endsWith('.md')) {
        for (const includePath of extractIncludePaths(content)) {
            const resolved = normalizePath(join(dirname(normalized), includePath));
            const includeLinks = await collectLinks(run, resolved, visited);

            links.push(...includeLinks);
        }
    }

    return links;
}

export function parseRegexps(patterns: unknown[]): RegExp[] {
    const result: RegExp[] = [];

    for (const pattern of patterns) {
        if (typeof pattern !== 'string') continue;

        const match = REGEXP_LITERAL.exec(pattern);

        try {
            result.push(match ? new RegExp(match[1], match[2]) : new RegExp(pattern));
        } catch {}
    }

    return result;
}

export function collectCrawlerExcludes(config: CrawlerConfig): {
    urls: string[];
    regexps: RegExp[];
} {
    const excludes = [config.crawler?.exclude, config['docs-viewer']?.crawler?.exclude];

    const urls = excludes.flatMap((exclude) =>
        Array.isArray(exclude?.url)
            ? exclude.url.filter((v): v is string => typeof v === 'string')
            : [],
    );

    const regexpPatterns = excludes.flatMap((exclude) =>
        Array.isArray(exclude?.regexp) ? exclude.regexp : [],
    );

    return {
        urls,
        regexps: parseRegexps(regexpPatterns),
    };
}

export function isFakeLink(url: string): boolean {
    const protocolMatch = /^([a-z][a-z0-9+.-]*):\/\//.exec(url);

    if (protocolMatch) {
        const scheme = protocolMatch[1];

        if (scheme !== 'http' && scheme !== 'https') {
            return true;
        }
    }

    if (url.startsWith('//')) {
        return true;
    }

    const nonSchemeProto = /^[+\w]{1,10}:(?!\/)/.exec(url);
    if (nonSchemeProto) {
        return true;
    }

    const withoutProtocol = protocolMatch ? url.slice(protocolMatch[0].length) : url;
    const host = withoutProtocol.split(/[/?#]/)[0];
    const hostWithoutPort = host.replace(/:\d+$/, '');

    if (IPV4_HOST_RE.test(host)) {
        return true;
    }

    if (hostWithoutPort.startsWith('*.')) {
        return true;
    }

    if (!protocolMatch && hostWithoutPort.endsWith('.md')) {
        return true;
    }

    return false;
}

export function crawlerNotifications(config: CrawlerConfig): CrawlerNotifications | undefined {
    const notifications =
        config.crawler?.notifications || config['docs-viewer']?.crawler?.notifications;

    if (!notifications) {
        return undefined;
    }

    const interval = notifications.interval;
    const emailReceivers = notifications.emailReceivers;
    const messengerReceivers = notifications.messengerReceivers;

    const hasEmail = Array.isArray(emailReceivers) && emailReceivers.length > 0;
    const hasMessenger = Array.isArray(messengerReceivers) && messengerReceivers.length > 0;

    if (!hasEmail && !hasMessenger) {
        return undefined;
    }

    const result: CrawlerNotifications = {
        interval: interval ?? 'weekly',
    };

    if (hasEmail) {
        result.emailReceivers = emailReceivers;
    }

    if (hasMessenger) {
        result.messengerReceivers = messengerReceivers;
    }

    return result;
}
